import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Chip,
  IconButton
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { createClockEvent } from '../../services/clockEventService';
import { workingHoursService } from '../../services/workingHoursService';
import { useDispatch } from 'react-redux';
import { createAuditLog } from '../../redux/thunks/auditThunks';
import { useAuth } from '../../context/AuthContext';

const MANAGER_PASSCODE = '1234';

const ClockInOutModal = ({ 
  open, 
  onClose, 
  user, 
  facilityId, 
  action, // 'clock_in' or 'clock_out'
  onSuccess,
  isAutoPrompt = false, // Whether this is an automatic prompt upon login
  shiftInfo = null // Information about the scheduled shift
}) => {
  const dispatch = useDispatch();
  const { logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [todayShift, setTodayShift] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [shiftStartTime, setShiftStartTime] = useState('');
  const [shiftEndTime, setShiftEndTime] = useState('');
  const [overtimeHours, setOvertimeHours] = useState('');
  const [overtimeReason, setOvertimeReason] = useState('');
  const [managerOverride, setManagerOverride] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState('');
  const [needsManagerOverride, setNeedsManagerOverride] = useState(false);

  const fetchTodayShift = useCallback(async () => {
    try {
      setLoading(true);
      const shift = await workingHoursService.getTodayWorkingHours(user.uid, facilityId);
      if (shift) {
        setTodayShift(shift);
        const start = new Date(shift.startTime);
        const end = new Date(shift.endTime);
        setShiftStartTime(start.toTimeString().slice(0, 5));
        setShiftEndTime(end.toTimeString().slice(0, 5));
      } else {
        // No scheduled shift for today
        setTodayShift(null);
        setShiftStartTime('');
        setShiftEndTime('');
      }
    } catch (error) {
      console.error('Error fetching today shift:', error);
      setError('Failed to fetch shift information');
    } finally {
      setLoading(false);
    }
  }, [user, facilityId]);

  const checkEarlyClockIn = useCallback(() => {
    if (!todayShift) return;
    
    const shiftStart = new Date(todayShift.startTime);
    const timeDiff = (shiftStart.getTime() - currentTime.getTime()) / (1000 * 60); // minutes
    
    if (timeDiff > 15) {
      setNeedsManagerOverride(true);
    } else {
      setNeedsManagerOverride(false);
    }
  }, [todayShift, currentTime]);

  useEffect(() => {
    if (open && user && facilityId) {
      fetchTodayShift();
      const timer = setInterval(() => setCurrentTime(new Date()), 1000);
      return () => clearInterval(timer);
    }
  }, [open, user, facilityId, fetchTodayShift]);

  useEffect(() => {
    if (action === 'clock_in' && todayShift && currentTime) {
      checkEarlyClockIn();
    }
  }, [action, todayShift, currentTime, checkEarlyClockIn]);

  const validateManagerPasscode = () => {
    return passcode === MANAGER_PASSCODE;
  };

  const handleClockAction = async () => {
    try {
      setLoading(true);
      setError('');

      // Validate manager override if needed
      if (needsManagerOverride && !managerOverride) {
        setError('Manager override required for early clock-in');
        return;
      }

      if (needsManagerOverride && managerOverride && !validateManagerPasscode()) {
        setError('Invalid manager passcode');
        return;
      }

      // Validate shift times if provided
      if (action === 'clock_in' && !shiftStartTime) {
        setError('Please confirm your shift start time');
        return;
      }

      if (action === 'clock_out' && !shiftEndTime) {
        setError('Please confirm your shift end time');
        return;
      }

      // Create clock event record
      const clockEvent = {
        userId: user.uid,
        facilityId: facilityId,
        action: action,
        timestamp: new Date().toISOString(),
        actualTime: currentTime.toISOString(),
        confirmedShiftStart: shiftStartTime ? `${new Date().toDateString()} ${shiftStartTime}` : null,
        confirmedShiftEnd: shiftEndTime ? `${new Date().toDateString()} ${shiftEndTime}` : null,
        overtimeHours: overtimeHours ? parseFloat(overtimeHours) : 0,
        overtimeReason: overtimeReason || '',
        managerOverride: managerOverride,
        scheduledShift: todayShift ? {
          startTime: todayShift.startTime,
          endTime: todayShift.endTime
        } : null,
        createdAt: new Date().toISOString()
      };

      // Save to clock_events collection via service
      await createClockEvent(clockEvent, facilityId);

      // Create audit log
      await dispatch(createAuditLog({
        userId: user.uid,
        action: `USER_${action.toUpperCase()}`,
        targetType: 'ATTENDANCE',
        details: {
          facilityId: facilityId,
          clockTime: currentTime.toISOString(),
          confirmedShiftStart: shiftStartTime,
          confirmedShiftEnd: shiftEndTime,
          overtimeHours: overtimeHours,
          overtimeReason: overtimeReason,
          managerOverride: managerOverride,
          needsManagerOverride: needsManagerOverride
        }
      }));

      // Update working hours if this is clock out with overtime
      if (action === 'clock_out' && overtimeHours && todayShift) {
        try {
          await workingHoursService.updateWorkingHours(todayShift.id, {
            overtimeHours: parseFloat(overtimeHours),
            overtimeReason: overtimeReason,
            actualEndTime: currentTime.toISOString(),
            updatedAt: new Date().toISOString()
          });
        } catch (error) {
          console.error('Error updating working hours:', error);
        }
      }

      onSuccess?.(action);
      onClose();

      // If this is a clock out, automatically sign the user out after a brief delay
      if (action === 'clock_out') {
        setTimeout(async () => {
          try {
            // Create audit log for automatic sign-out
            await dispatch(createAuditLog({
              userId: user.uid,
              action: 'USER_AUTO_SIGNOUT',
              targetType: 'AUTHENTICATION',
              details: {
                facilityId: facilityId,
                reason: 'Automatic sign-out after clock out',
                clockOutTime: currentTime.toISOString()
              }
            }));
            
            await logout();
          } catch (error) {
            console.error('Error signing out after clock out:', error);
          }
        }, 2000); // 2 second delay to allow success message to show
      }

    } catch (error) {
      console.error(`Error during ${action}:`, error);
      setError(`Failed to ${action.replace('_', ' ')}`);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
  };

  const getTimeUntilShift = () => {
    if (!todayShift) return null;
    const shiftStart = new Date(todayShift.startTime);
    const timeDiff = (shiftStart.getTime() - currentTime.getTime()) / (1000 * 60); // minutes
    
    if (timeDiff > 0) {
      const hours = Math.floor(timeDiff / 60);
      const minutes = Math.floor(timeDiff % 60);
      return `${hours}h ${minutes}m until shift starts`;
    }
    return null;
  };

  if (loading && !todayShift) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading shift information...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <TimeIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Box>
              <Typography variant="h6">
                {isAutoPrompt ? 'Ready to Clock In?' : (action === 'clock_in' ? 'Clock In' : 'Clock Out')}
              </Typography>
              {isAutoPrompt && (
                <Typography variant="body2" color="text.secondary">
                  You have a scheduled shift starting soon
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Current Time Display */}
        <Paper elevation={1} sx={{ p: 2, mb: 2, backgroundColor: 'primary.main', color: 'white' }}>
          <Typography variant="h4" textAlign="center">
            {formatTime(currentTime)}
          </Typography>
          <Typography variant="body2" textAlign="center">
            {currentTime.toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </Typography>
        </Paper>

        {/* Shift Information */}
        {todayShift ? (
          <Box mb={2}>
            <Typography variant="subtitle1" gutterBottom>
              Today's Scheduled Shift:
            </Typography>
            <Box display="flex" gap={1} mb={1}>
              <Chip 
                label={`Start: ${shiftStartTime}`} 
                variant="outlined" 
                size="small" 
              />
              <Chip 
                label={`End: ${shiftEndTime}`} 
                variant="outlined" 
                size="small" 
              />
            </Box>
            {action === 'clock_in' && getTimeUntilShift() && (
              <Typography variant="body2" color="text.secondary">
                {getTimeUntilShift()}
              </Typography>
            )}
          </Box>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            No scheduled shift found for today. Please confirm your working hours below.
          </Alert>
        )}

        {/* Shift Confirmation */}
        <Box mb={2}>
          <Typography variant="subtitle2" gutterBottom>
            Confirm Shift Hours:
          </Typography>
          <Box display="flex" gap={2}>
            <TextField
              label="Start Time"
              type="time"
              value={shiftStartTime}
              onChange={(e) => setShiftStartTime(e.target.value)}
              disabled={action === 'clock_out'}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Time"
              type="time"
              value={shiftEndTime}
              onChange={(e) => setShiftEndTime(e.target.value)}
              disabled={action === 'clock_in'}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Box>

        {/* Overtime Section - Only for Clock Out */}
        {action === 'clock_out' && (
          <Box mb={2}>
            <Divider sx={{ my: 2 }} />
            
            {/* Sign-out notice */}
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Note:</strong> You will be automatically signed out after clocking out for security purposes.
              </Typography>
            </Alert>
            
            <Typography variant="subtitle2" gutterBottom>
              Overtime Hours (if any):
            </Typography>
            <TextField
              label="Overtime Hours"
              type="number"
              value={overtimeHours}
              onChange={(e) => setOvertimeHours(e.target.value)}
              size="small"
              fullWidth
              sx={{ mb: 2 }}
              inputProps={{ min: 0, max: 12, step: 0.5 }}
            />
            {overtimeHours && (
              <TextField
                label="Reason for Overtime"
                multiline
                rows={2}
                value={overtimeReason}
                onChange={(e) => setOvertimeReason(e.target.value)}
                size="small"
                fullWidth
                placeholder="Please explain the reason for overtime..."
              />
            )}
          </Box>
        )}

        {/* Manager Override Section */}
        {needsManagerOverride && action === 'clock_in' && (
          <Box mb={2}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Box display="flex" alignItems="center">
                <WarningIcon sx={{ mr: 1 }} />
                <Typography variant="body2">
                  You are clocking in more than 15 minutes before your shift. 
                  Manager override required.
                </Typography>
              </Box>
            </Alert>
            
            <TextField
              label="Manager Override Passcode"
              type="password"
              value={passcode}
              onChange={(e) => {
                setPasscode(e.target.value);
                setManagerOverride(e.target.value === MANAGER_PASSCODE);
              }}
              size="small"
              fullWidth
              error={Boolean(passcode && !validateManagerPasscode())}
              helperText={
                passcode && !validateManagerPasscode() 
                  ? "Invalid passcode" 
                  : "Enter manager override passcode"
              }
            />
            
            {managerOverride && (
              <Box display="flex" alignItems="center" mt={1}>
                <CheckIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="body2" color="success.main">
                  Manager override approved
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {isAutoPrompt ? 'Clock In Later' : 'Cancel'}
        </Button>
        <Button
          onClick={handleClockAction}
          variant="contained"
          disabled={loading || (needsManagerOverride && !managerOverride)}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Processing...' : (action === 'clock_in' ? 'Clock In Now' : 'Clock Out')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClockInOutModal; 